import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import { Discount } from '@dataTypes/discount.type'
import {
  getAllDiscounts as getAllDiscountsApi,
  addDiscount as addDiscountApi,
  updateDiscount as updateDiscountApi,
  deleteDiscount as deleteDiscountApi,
  CreateDiscountRequest,
  DiscountListResponse,
  DiscountQueryFilters,
  UpdateDiscountRequest,
} from '@services/api'
import { RootState } from '@store/index'

export const getAllDiscount = createAsyncThunk<
  DiscountListResponse,
  DiscountQueryFilters | void,
  { rejectValue: string; state: RootState }
>(
  'api/discounts/get/all',
  async (queryFilters, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const clinicId =
        state.clinic.selectedClinic?.clinic_id ||
        localStorage.getItem('clinic_id')

      const filters: DiscountQueryFilters = {
        ...(queryFilters || {}),
      }
      if (clinicId) {
        filters.clinic_id = clinicId
      }

      const response = await getAllDiscountsApi(
        Object.keys(filters).length > 0 ? filters : undefined,
      )
      return response
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch discount'
      return rejectWithValue(errorMessage)
    }
  },
)

export const addDiscount = createAsyncThunk<
  Discount,
  Discount,
  { rejectValue: string }
>('api/discounts/add', async (newDiscount, { rejectWithValue }) => {
  try {
    const payload: CreateDiscountRequest = {
      status: newDiscount.status,
      description: newDiscount.description,
      discount_percentage: newDiscount.discount_percentage,
      clinic_id: newDiscount.clinic_id || '',
    }
    const discount = await addDiscountApi(payload)
    return discount
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to add discount'
    return rejectWithValue(errorMessage)
  }
})

export const updateDiscount = createAsyncThunk<
  Discount,
  { id: string; body: Partial<Discount> },
  { rejectValue: string }
>('api/discounts/update', async ({ id, body }, { rejectWithValue }) => {
  try {
    const payload: UpdateDiscountRequest = {
      discount_id: id,
      ...(body.status !== undefined && { status: body.status }),
      ...(body.description !== undefined && { description: body.description }),
      ...(body.discount_percentage !== undefined && {
        discount_percentage: body.discount_percentage,
      }),
      ...(body.clinic_id !== undefined &&
        body.clinic_id !== null && { clinic_id: body.clinic_id }),
    }
    const discount = await updateDiscountApi(payload)
    return discount
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to update discount'
    return rejectWithValue(errorMessage)
  }
})

export const removeDiscount = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/discounts/remove', async (id, { rejectWithValue }) => {
  try {
    await deleteDiscountApi(id)
    return id
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to delete discount'
    return rejectWithValue(errorMessage)
  }
})

export const updateDiscountDraft = createAction<Discount[]>(
  'api/discounts/update/draft',
)
